import { Button, ButtonProps, Stack } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type LoadMoreProps = {
    // Return the request's promise, so no new request starts while it is in flight.
    loadMore: () => void | Promise<unknown>;
    buttonProps?: Omit<ButtonProps, 'onClick'>;
};
const LoadMore = ({ loadMore, buttonProps }: LoadMoreProps) => {
    const [isInView, setIsInView] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                // A batch can hold stale entries from a frame before the latest layout: the last one is current.
                setIsInView(entries[entries.length - 1].isIntersecting);
            },
            { threshold: 0.5 },
        );

        const element = ref.current;
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []);

    // Consumers pass a new loadMore on every render, so this effect runs on every render while in view.
    // Load only when no request is in flight. When the request settles, the state change re-runs the
    // effect with the latest props, and loading continues if the sentinel is still in view.
    useEffect(() => {
        if (isInView && !isLoading) {
            setIsLoading(true);
            Promise.resolve(loadMore()).finally(() => setIsLoading(false));
        }
    }, [isInView, isLoading, loadMore]);

    return (
        <Stack>
            {!buttonProps ? (
                <Stack ref={ref} height={4} />
            ) : (
                <Button {...buttonProps} onClick={loadMore}>
                    {t('str.load-more')}
                </Button>
            )}
        </Stack>
    );
};

export default LoadMore;
